"use client";

import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";

/**
 * Scroll-triggered entrance for a block of content.
 *
 * Renders a plain wrapper with the `reveal` class (opacity 0, 14 px low) and
 * adds `is-visible` once it enters the viewport — the transition itself is
 * CSS (see globals.css), so this ships no animation library and costs one
 * IntersectionObserver per element. It degrades safely:
 *   - no JS → the server HTML never gets the class, so a tiny inline check
 *     marks <html class="no-js"> and the CSS shows everything;
 *   - prefers-reduced-motion → CSS disables the transition entirely;
 *   - already in view on load → revealed on the first observer callback.
 *
 * `delay` (ms) staggers siblings, e.g. cards in a grid.
 */
export function Reveal({
  children,
  className = "",
  delay = 0,
  as: Tag = "div",
  style,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  as?: "div" | "section" | "article" | "li";
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      el.classList.add("is-visible");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            el.classList.add("is-visible");
            io.disconnect();
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const T = Tag as "div";
  return (
    <T
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`reveal ${className}`}
      style={
        delay
          ? ({ ...style, "--reveal-delay": `${delay}ms` } as CSSProperties)
          : style
      }
    >
      {children}
    </T>
  );
}
